/**
 * Accordion class
 *
 * Handles each individual accordion item.
 */
class Accordion {
	/**
	 * Constructor.
	 *
	 * @param {HTMLElement} domNode the accordion dom node
	 */
	constructor( domNode ) {
		this.accordion = domNode;

		// Ensure the same accordion is not instantiated more than once.
		if ( this.accordion.dataset.initiated ) {
			return;
		}
		this.button = this.accordion.querySelector( 'button[aria-expanded]' );
		this.content = this.accordion.querySelector( '.wp-block-qala-blocks-single-accordion__content' );

		// Headerless accordion with no title: no content to operate on.
		if ( ! this.content ) {
			return;
		}

		// Markup always renders closed; default-open is applied below via toggle().
		this.openState = this.button
			? this.button.getAttribute( 'aria-expanded' ) === 'true'
			: false;
		this.imageId = this.accordion.dataset.accordionImageId;

		// add event listeners
		if ( this.button ) {
			this.button.addEventListener( 'click', this.onButtonClick );
		}

		//get the multi-accordion containers data-multi-accordion-id attribute
		//this will be used to check if the accordion is part of a multi-accordion
		this.containerId = this.accordion.closest( '.wp-block-qala-blocks-multi-image-accordions' )?.dataset.multiAccordionId;

		if ( this.containerId ) {
			// We are in a multi-accordion block.
			// Listen for the singleAccordionOpened event.
			wp.hooks.addAction( 'qalaBlocks.singleAccordionOpened', 'qala-blocks', this.handleOpenAccordion );
		}

		// Initialize the accordion to the correct state.
		if ( this.accordion.dataset.defaultOpen === 'true' ) {
			this.toggle( true );
		}

		// Set this as having been initiated.
		this.accordion.dataset.initiated = true;
	}

	/**
	 * Handle the singleAccordionOpened event.
	 * If another accordion is opened and this is already open, close it.
	 *
	 * @param {string} imageId The image ID to be shown.
	 * @param {string} containerId The container ID of the multi-accordion.
	 */
	handleOpenAccordion = ( imageId, containerId ) => {
		if ( containerId === this.containerId && imageId !== this.imageId && this.openState ) {
			this.toggle( ! this.openState );
		}
	}

	/**
	 * Run on clicking the button.
	 *
	 * @param {Event} event The event triggered with the click.
	 */
	onButtonClick = event => {
		event.preventDefault();
		this.userToggle( ! this.openState );
	};

	/**
	 * Toggle from a user gesture (header button or external toggle button).
	 *
	 * Also dispatches the multi-accordion action so the parent block can swap images and
	 * close siblings. Kept out of toggle(), which also runs programmatically (default-open,
	 * sibling close) where firing the action would loop.
	 *
	 * @param {boolean} openState Open state of the accordion.
	 */
	userToggle = openState => {
		this.toggle( openState );
		if ( this.containerId ) {
			wp.hooks.doAction( 'qalaBlocks.accordionToggle', this.imageId, this.containerId, this.openState );
		}
	};

	/**
	 * Toggle open/closed state of the accordion.
	 *
	 * @param {boolean} openState Open state of the accordion.
	 */
	toggle = openState => {
		// don't do anything if the openState state doesn't change.
		if ( openState === this.openState ) {
			return;
		}

		// Toggle active class on the parent.
		this.accordion.classList.toggle( 'active' );
		// update the internal state
		this.openState = openState;

		// handle DOM updates
		if ( this.button ) {
			this.button.setAttribute( 'aria-expanded', openState );
		}
		if ( openState ) {
			this.toggleOpenState();
		} else {
			this.toggleClosedState();
		}
	}

	/**
	 * Initialize anchor link listeners.
	 *
	 * Qala Theme already listens to links with an anchor hash and uses a scrollTo animation
	 * which takes into account the header height. So keep it simple and just open the accordion here.
	 */
	initAnchorLinks() {
		const accordionId = this.accordion.id;

		if ( ! accordionId ) {
			return;
		}

		// Find all anchor links on the page that link to this accordion.
		const anchorLinks = document.querySelectorAll( `a[href$="#${ accordionId }"]` );

		anchorLinks.forEach( link => {
			// make sure the link is for this page.
			if ( link.pathname !== window.location.pathname ) {
				return;
			}

			// Toggle buttons are wired separately (open and close); skip the open-only anchor behaviour.
			if ( link.closest( '[data-accordion-toggle]' ) ) {
				return;
			}
			link.addEventListener( 'click', () => {
				// it is definitely a link to this accordion on this page, so just open it.
				if ( ! this.openState ) {
					this.toggle( true );
				}
			} );
		} );
	}

	/**
	 * Check URL hash on page load and on hash changes
	 */
	checkUrlHash() {
		const accordionId = this.accordion.id;
		const doc = this.accordion.ownerDocument;

		if ( ! accordionId ) {
			return;
		}

		const handleHashMatch = () => {
			if ( window.location.hash === `#${ accordionId }` && ! this.openState ) {
				this.toggle( true );
			}
		};

		// Handle the page loads.
		if ( window.location.hash === `#${ accordionId }` ) {
			if ( doc.readyState === 'loading' ) {
				doc.addEventListener( 'DOMContentLoaded', handleHashMatch );
			} else {
				handleHashMatch();
			}
		}

		// Handle Back/Forward browser buttons
		doc.addEventListener( 'hashchange', handleHashMatch );
	}

	/**
	 * Toggle its open state.
	 */
	toggleOpenState = () => {
		this.content.removeAttribute( 'hidden' );
		this.content.classList.remove( 'wp-block-qala-blocks-single-accordion__content--hidden' );
	}

	/**
	 * Toggle its closed state.
	 */
	toggleClosedState = () => {
		this.content.setAttribute( 'hidden', true );
		this.content.classList.add( 'wp-block-qala-blocks-single-accordion__content--hidden' );
	}
}

/**
 * Wire up external buttons that toggle an accordion.
 *
 * A button opts in via the core/button "Accordion toggle" control (adds
 * data-accordion-toggle) and links to the accordion's anchor (e.g. href="#faq-1"); that
 * hash is the target. Opens and closes, keeping aria-expanded and the state icon in sync.
 *
 * @param {Object} registry Map of accordion id to Accordion instance.
 */
function initAccordionToggleButtons( registry ) {
	const wrappers = document.querySelectorAll( '.wp-block-button[data-accordion-toggle]' );

	wrappers.forEach( wrapper => {
		if ( wrapper.dataset.accordionToggleInitiated ) {
			return;
		}

		const link = wrapper.querySelector( 'a[href*="#"]' );
		if ( ! link ) {
			return;
		}

		// The target accordion id is the hash of the button's link.
		const targetId = link.hash ? link.hash.substring( 1 ) : '';
		const accordion = targetId ? registry[ targetId ] : null;
		if ( ! accordion ) {
			return;
		}

		const closedIcon = wrapper.dataset.accordionToggleIcon || '';
		const openIcon = wrapper.dataset.accordionToggleIconOpen || '';

		// Accessible disclosure semantics on the link.
		link.setAttribute( 'role', 'button' );
		link.setAttribute( 'aria-controls', accordion.content.id );

		let iconEl = null;
		if ( closedIcon || openIcon ) {
			iconEl = document.createElement( 'img' );
			iconEl.className = 'wp-block-button__accordion-toggle-icon';
			iconEl.setAttribute( 'aria-hidden', 'true' );
			iconEl.alt = '';
			link.appendChild( iconEl );
		}

		const syncState = openState => {
			link.setAttribute( 'aria-expanded', openState ? 'true' : 'false' );
			wrapper.classList.toggle( 'is-accordion-open', openState );
			if ( iconEl ) {
				const nextIcon = openState ? openIcon || closedIcon : closedIcon || openIcon;
				if ( nextIcon ) {
					iconEl.src = nextIcon;
				}
			}
		};

		// Sync however the accordion toggles (this button, hidden header, anchor link, sibling close).
		const observer = new MutationObserver( () => {
			syncState( accordion.accordion.classList.contains( 'active' ) );
		} );
		observer.observe( accordion.accordion, { attributes: true, attributeFilter: [ 'class' ] } );

		syncState( accordion.openState );

		const handleToggle = event => {
			event.preventDefault();

			const willOpen = ! accordion.openState;

			// On close, block the theme's DOMContentLoaded anchor-scroll (disorienting when collapsing);
			// on open, let it run so the revealed content scrolls into view.
			if ( ! willOpen ) {
				event.stopImmediatePropagation();
			}

			accordion.userToggle( willOpen );
		};

		link.addEventListener( 'click', handleToggle );
		// role="button" should also respond to Space (Enter already activates links).
		link.addEventListener( 'keydown', event => {
			if ( ' ' === event.key || 'Spacebar' === event.key ) {
				handleToggle( event );
			}
		} );

		wrapper.dataset.accordionToggleInitiated = true;
	} );
}

// Instantiate each accordion, keyed by anchor id so external toggle buttons can reach it.
const accordionRegistry = {};
const accordions = document.querySelectorAll( '.wp-block-qala-blocks-single-accordion' );
accordions.forEach( accordion => {
	const accordionInstance = new Accordion( accordion );

	accordionInstance.initAnchorLinks();
	accordionInstance.checkUrlHash();

	// Skip instances whose constructor bailed early (no content for a toggle button to reach).
	if ( accordion.id && accordionInstance.content ) {
		accordionRegistry[ accordion.id ] = accordionInstance;
	}
} );

initAccordionToggleButtons( accordionRegistry );
